import { useMemo } from "react"
import {
  CompanyConnectorDto,
  ConnectionStatus,
  ConnectorDto,
} from "~/ui-rtk/api/types"
import { useDeepCompareMemo } from "~/ui-rtk/hooks/deep-compare-memo"
import { isConnectorExcluded } from "~/ui-rtk/utils/connector-utils"
import { checkForMissingKeywords } from "./utils"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useConnect } from "~/ui-rtk/components/features/connectors/ConnectorsTable/connect"

export interface TConnectorSetupDetails {
  totalConnectors: number
  totalConnected: number
  hasMissingKeywords: boolean
  requiresReconnect: CompanyConnectorDto[]
  essentialUnconnectedConnectors: ConnectorDto[]
  totalEssentialConnectors: number
  totalEssentialConnected: number
}

export const useSetupDetails = (
  connectors: ConnectorDto[] | undefined,
  companyConnectors: CompanyConnectorDto[] | undefined,
): TConnectorSetupDetails => {
  const currentCompany = useCurrentCompany()
  const { totalEssentialConnectorsAmount, essentialConnectorsConnectedAmount } =
    useConnect()

  const memoizedConnectors = useDeepCompareMemo(connectors)
  const memoizedCompanyConnectors = useDeepCompareMemo(companyConnectors)

  const connectorsSetupDetails = useMemo<
    Omit<TConnectorSetupDetails, "hasMissingKeywords">
  >(() => {
    if (!memoizedConnectors || !memoizedCompanyConnectors) {
      return {
        totalConnectors: 0,
        totalConnected: 0,
        requiresReconnect: [],
        essentialUnconnectedConnectors: [],
        totalEssentialConnectors: 0,
        totalEssentialConnected: 0,
      }
    }

    const requiresReconnect: CompanyConnectorDto[] = []

    memoizedCompanyConnectors.forEach(service => {
      if (
        !isConnectorExcluded(service.serviceName) &&
        service.status === ConnectionStatus.PENDING_RECONNECT
      ) {
        requiresReconnect.push(service)
      }
    })

    // Get all essential connectors
    const essentialConnectors = memoizedConnectors.filter(c =>
      c.category.includes("Essential"),
    )

    const essentialUnconnectedConnectors = essentialConnectors.filter(c => {
      const companyConnector = memoizedCompanyConnectors.find(
        service => service.serviceName === c.id,
      )
      // Return as unconnected if not found OR if status is not CONNECTED
      return (
        !companyConnector ||
        companyConnector.status !== ConnectionStatus.CONNECTED
      )
    })

    // Calculate essential connector metrics - only count CONNECTED status
    const totalEssentialConnectors = essentialConnectors.length
    const totalEssentialConnected = essentialConnectors.filter(c => {
      const companyConnector = memoizedCompanyConnectors.find(
        service => service.serviceName === c.id,
      )
      return (
        companyConnector &&
        companyConnector.status === ConnectionStatus.CONNECTED
      )
    }).length

    return {
      essentialUnconnectedConnectors,
      requiresReconnect,
      totalConnected: essentialConnectorsConnectedAmount,
      totalConnectors: totalEssentialConnectorsAmount,
      totalEssentialConnectors,
      totalEssentialConnected,
    }
  }, [
    memoizedConnectors,
    memoizedCompanyConnectors,
    essentialConnectorsConnectedAmount,
    totalEssentialConnectorsAmount,
  ])

  const hasMissingKeywords = useMemo(() => {
    const keywords = currentCompany?.metadata?.keywords
    if (!keywords) {
      return true
    }

    return checkForMissingKeywords(
      keywords as Record<string, Record<string, unknown[]>>,
    )
  }, [currentCompany])

  return {
    ...connectorsSetupDetails,
    hasMissingKeywords,
  }
}
